import { useSyncExternalStore } from 'react';

/**
 * Global offline strip — ported from `Vault Empty & States.dc.html` (F3 · OFFLINE):
 * amber-tinted bar, 7px amber dot, bold amber-ink label, muted explainer, and an
 * optional right-aligned mono "LAST SYNC …" readout.
 *
 * Purely signal-driven: renders only while `navigator.onLine === false`, tracked
 * through the `online` / `offline` window events via `useSyncExternalStore`
 * (no timers, no module-scope `Date.now()` / `Math.random()`).
 */

function subscribe(onStoreChange: () => void): () => void {
  window.addEventListener('online', onStoreChange);
  window.addEventListener('offline', onStoreChange);
  return () => {
    window.removeEventListener('online', onStoreChange);
    window.removeEventListener('offline', onStoreChange);
  };
}

const getSnapshot = (): boolean => navigator.onLine;
// SSR / first-paint fallback: assume online so the banner never flashes.
const getServerSnapshot = (): boolean => true;

export interface OfflineBannerProps {
  /** Optional right-aligned mono readout, e.g. "LAST SYNC 41 MIN AGO". */
  lastSyncLabel?: string;
}

export function OfflineBanner({ lastSyncLabel }: OfflineBannerProps) {
  const online = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  if (online) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 20px',
        // amber tint layered over the app bg so the fixed strip stays opaque
        background: 'linear-gradient(var(--amber-tint), var(--amber-tint)), var(--bg)',
        borderBottom: '1px solid var(--amber-line)',
        fontFamily: 'var(--font-body)',
        boxSizing: 'border-box',
      }}
    >
      <span
        aria-hidden
        style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--amber)', flexShrink: 0 }}
      />
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--amber-ink)', whiteSpace: 'nowrap' }}>
        You’re offline
      </span>
      <span style={{ fontSize: 12, color: 'var(--text-2)', minWidth: 0 }}>
        — changes save locally and sync when you’re back.
      </span>
      {lastSyncLabel && (
        <span
          style={{
            marginLeft: 'auto',
            fontFamily: 'var(--font-mono)',
            fontSize: 9.5,
            color: 'var(--text-3)',
            whiteSpace: 'nowrap',
          }}
        >
          {lastSyncLabel}
        </span>
      )}
    </div>
  );
}
